import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Card, Typography, Button, CircularProgress, Alert, Divider, Chip, Grid, useTheme, alpha,
  Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import config from "../../../config";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const WPReanalyze = () => {
  const { store_id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [analysisResult, setAnalysisResult] = useState(null);

  const handleReanalyze = async () => {
    setAnalyzing(true);
    setError(null);
    setAnalysisResult(null);
    setSuccess(null);

    try {
      const response = await fetch(`${config.API_BASE_URL}/client/pos/wordpress/re-analyze`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          client_store_id: store_id
        })
      });

      const result = await response.json();
      if (result.success && result.data) {
        setAnalysisResult(result.data);
      } else {
        setError(result.message || "Analysis failed.");
      }
    } catch (err) {
      setError("Failed to connect to WooCommerce for analysis.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!analysisResult) return;

    setSaving(true);
    setError(null);

    try {
      // Build the new wc_config from analysis
      // Note: we preserve consumer_secret in it for testing endpoints later
      const newWcConfig = {
        mood_source: analysisResult.mood_source || "none",
        mood_key: analysisResult.mood_key,
        allowed_moods: analysisResult.detected_moods || [],
        potency_enabled: !!analysisResult.potency_key,
        potency_source: analysisResult.potency_key,
        cbd_source: "none"
      };

      const response = await fetch(`${config.API_BASE_URL}/client/pos/wordpress/update-wc-config`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          client_store_id: store_id,
          wc_config: newWcConfig
        })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess("WooCommerce configuration updated successfully!");
        setTimeout(() => navigate("/pos-sync"), 2000);
      } else {
        setError(result.message || "Failed to update configuration.");
      }
    } catch (err) {
      setError("An error occurred while saving the configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/pos-sync")} sx={{ mr: 2 }}>Back</Button>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Re-run AI Analysis</Typography>
      </Box>

      <Card sx={{ p: 4, borderRadius: 3, boxShadow: "0 10px 40px -10px rgba(0,0,0,0.1)" }}>
        <Typography variant="body1" color="text.secondary" mb={3}>
          If you recently added new metadata fields (like potency or effects) to your WooCommerce products,
          run the AI Analysis again so Bud-i can discover them!
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleOutlineIcon />}>{success}</Alert>}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "center" }}>

          <Button
            variant="outlined"
            size="large"
            startIcon={analyzing ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
            onClick={handleReanalyze}
            disabled={analyzing || saving}
            sx={{ py: 1.5, px: 4, borderRadius: 2, fontWeight: 700 }}
          >
            {analyzing ? "Analyzing WooCommerce Store..." : "Run AI Analysis"}
          </Button>

          {analysisResult && (
            <Box sx={{ width: "100%", mt: 2, p: 4, bgcolor: '#ffffff', borderRadius: 4, border: '1px solid', borderColor: 'grey.200', boxShadow: '0 12px 40px rgba(0,0,0,0.04)' }}>
              <Typography variant="h6" color="text.primary" fontWeight="800" mb={2} letterSpacing="-0.3px">
                Analysis Complete
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Mood Detection Source:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1), px: 1, py: 0.5, borderRadius: 1 }}>
                    {analysisResult.mood_source === "none" ? "Not Found" : analysisResult.mood_source}
                  </Typography>
                </Box>

                {analysisResult.detected_moods && analysisResult.detected_moods.length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: 'text.secondary' }}>Discovered Moods:</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {analysisResult.detected_moods.map((m, i) => (
                        <Chip key={i} label={m} size="small" sx={{ fontWeight: 600, textTransform: 'capitalize', bgcolor: '#ffffff', border: '1px solid', borderColor: 'divider' }} />
                      ))}
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 1, borderColor: alpha(theme.palette.divider, 0.5) }} />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Potency (THC) Meta Key:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: analysisResult.potency_key ? 'primary.main' : 'error.main', bgcolor: analysisResult.potency_key ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.error.main, 0.1), px: 1, py: 0.5, borderRadius: 1 }}>
                    {analysisResult.potency_key || "Not Found"}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                fullWidth
                color="primary"
                onClick={handleSaveConfig}
                disabled={saving}
                sx={{ py: 1.5, borderRadius: 2, fontWeight: 700 }}
              >
                {saving ? "Saving Configuration..." : "Confirm & Save Configuration"}
              </Button>
            </Box>
          )}

          {/* How to configure section */}
          <Box sx={{ width: "100%", mt: 4, borderRadius: 4, overflow: 'hidden' }}>
            <Accordion sx={{ boxShadow: '0 12px 40px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'grey.200', borderRadius: 4, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), borderBottom: '1px solid', borderColor: 'grey.100' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    <SettingsSuggestIcon color="primary" />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.3px' }}>
                      How to format your WooCommerce data
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Click to view instructions on adding data to your WordPress Admin panel.
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0 }}>
                {/* Steps */}
                <Box sx={{ p: 4 }}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', color: 'grey.500', mb: 2, fontSize: '0.75rem', letterSpacing: '1px' }}>
                        Step-by-Step Guide
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800, flexShrink: 0 }}>1</Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>Go to your WordPress Admin Dashboard and click on <strong>Products</strong>.</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800, flexShrink: 0 }}>2</Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}><strong>Edit</strong> the product you want to configure.</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800, flexShrink: 0 }}>3</Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>Scroll down to the <strong>Custom Fields</strong> section (if you don't see it, enable it in "Screen Options" at the top right).</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800, flexShrink: 0 }}>4</Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>Click <strong>Add New</strong> and enter the exact Names and Values shown to the right.</Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Data Map */}
                    <Grid item xs={12} md={7}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', color: 'grey.500', mb: 2, fontSize: '0.75rem', letterSpacing: '1px' }}>
                        Required Custom Fields
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Mood Box */}
                        <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', position: 'relative', overflow: 'hidden' }}>
                          <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '4px', bgcolor: 'primary.main' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 1.5 }}>Moods / Effects <span style={{ color: theme.palette.error.main }}>*</span></Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={5}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Name</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>moods</Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Value (comma separated)</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>relaxed, happy, sleepy</Typography>
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Potency Box % */}
                        <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', position: 'relative', overflow: 'hidden' }}>
                          <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '4px', bgcolor: 'info.main' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 1.5 }}>Potency Filter (%) <span style={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '0.75rem' }}>(Optional)</span></Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={5}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Name (pct)</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>thc_content_pct</Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Value (numeric only)</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>24.5</Typography>
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Potency Box mg */}
                        <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', position: 'relative', overflow: 'hidden' }}>
                          <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '4px', bgcolor: 'info.main' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 1.5 }}>Potency Filter (mg) <span style={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '0.75rem' }}>(Optional)</span></Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={5}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Name (mg)</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>thc_content_mg</Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Value (numeric only)</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>100</Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>


        </Box>
      </Card>
    </Box>
  );
};

export default WPReanalyze;
